import React, { useState, useEffect, useMemo } from 'react';
import { Download } from 'lucide-react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../Common/context/ToastContext';

const baseUrl = process.env.REACT_APP_API || '';


function TaxInvoice() {
  const { showToast } = useToast();
  const formatDate = (d) =>
    `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

  const getDefaultMonth = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getDefaultMonth());
  const [adminList, setAdminList] = useState([]);
  const [loading, setLoading] = useState(false);

  const quickPeriod = (type) => {
    const today = new Date();
    let month;
    switch (type) {
      case '당월':
        month = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        break;
      case '전월':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        month = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
        break;
      default:
        return;
    }
    setSelectedMonth(month);
  };

  useEffect(() => {
    if (!selectedMonth || !selectedMonth.match(/^\d{4}-\d{2}$/)) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(baseUrl + '/api/admin/cp-settlement/monthly-billing', {
          params: {
            month: selectedMonth,
            includeEmpty: 0,
            includeDetails: 1,
          },
          withCredentials: true,
        });
        let rows = [];
        if (res?.data?.code === 1000) {
          const data = res.data?.data;
          rows = Array.isArray(data?.rows) ? data.rows : [];
        }
        setAdminList(rows);
      } catch (e) {
        showToast(e?.response?.data?.msg || '세금계산서 데이터를 불러오는 중 오류가 발생했습니다.', 'error');
        setAdminList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth, showToast]);

  // 상세 내역용 공통 매핑 함수 (결제/정산/취소 공통 필드 정규화)
  const normalizeDetailRow = (row) => {
    const rawDate =
      row.approvalDate ??
      row.approval_date ??
      row.paymentDate ??
      row.payment_date ??
      row.settlementDate ??
      row.settlement_date ??
      row.createdAt ??
      row.created_at ??
      row.date ??
      row.trade_date ??
      '';

    const date = rawDate ? dayjs(rawDate).format('YYYY-MM-DD') : '-';

    // 약국명 (승인번호 대신 표시)
    const pharmacyName =
      row.pharmacy_name ??
      row.pharmacyName ??
      row.merchant_name ??
      row.cp_name ??
      row.cpName ??
      '';

    const paymentAmount = Number(
      row.paymentAmount ??
        row.payment_amount ??
        row.trd_amt ??
        row.amount ??
        row.payment_completed_amount ??
        0
    );

    const fee = Number(
      row.fee ??
        row.fee_amount ??
        row.cp_fee_amount ??
        row.settlement_fee_amount ??
        row.payment_cp_fee_amount ??
        0
    );

    let settlementAmount = Number(
      row.settlementAmount ??
        row.settlement_amount ??
        row.net_amount ??
        row.settlement_net_amount ??
        0
    );

    // 세부 상세에 net 값이 없고 trd_amt / cp_fee_amount만 있는 경우 직접 계산
    if (!settlementAmount && row.trd_amt != null) {
      const trd = Number(row.trd_amt ?? 0);
      const cpFee = Number(row.cp_fee_amount ?? 0);
      settlementAmount = trd - cpFee;
    }

    return {
      date,
      pharmacyName,
      paymentAmount: Math.round(paymentAmount),
      fee: Math.round(fee),
      settlementAmount: Math.round(settlementAmount),
    };
  };

  const groupedList = useMemo(() => {
    return adminList.map((row) => {
      const cpId = row.cp_id ?? row.cpId ?? '';
      const cpName = row.cp_name ?? row.cpName ?? '-';
      // API 응답 값이 문자열일 수 있으므로 Number()로 변환 (소수점 포함 값도 처리)
      const paymentAmount = Number(row.payment_completed_amount ?? row.paymentCompletedAmount ?? 0);
      const fee = Number(row.payment_cp_fee_amount ?? row.paymentCpFeeAmount ?? row.settlement_fee_amount ?? row.settlementFeeAmount ?? 0);
      const settlementAmount = Number(row.settlement_net_amount ?? row.settlementNetAmount ?? 0);

      const paymentDetails = Array.isArray(row.payment_details)
        ? row.payment_details
        : [];
      const settlementDetails = Array.isArray(row.settlement_details)
        ? row.settlement_details
        : [];
      const cancelDetails = Array.isArray(row.cancel_details)
        ? row.cancel_details
        : [];

      let details = [
        ...paymentDetails.map(normalizeDetailRow),
        ...settlementDetails.map(normalizeDetailRow),
        ...cancelDetails.map(normalizeDetailRow),
      ];

      // 날짜 기준 내림차순 정렬
      details.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

      return {
        id: String(cpId),
        name: cpName,
        paymentAmount: Math.round(paymentAmount), // 소수점 제거
        fee: Math.round(fee), // 소수점 제거
        settlementAmount: Math.round(settlementAmount), // 소수점 제거
        details,
      };
    });
  }, [adminList]);

  // 통계: 전체 결제금액 / 수수료 / 정산금액 / 가맹점 수
  const summaryStats = useMemo(() => {
    const totalPayment = groupedList.reduce((sum, item) => sum + Number(item.paymentAmount || 0), 0);
    const totalFee = groupedList.reduce((sum, item) => sum + Number(item.fee || 0), 0);
    const totalSettlement = groupedList.reduce((sum, item) => sum + Number(item.settlementAmount || 0), 0);
    const merchantCount = groupedList.length;

    return {
      totalPayment,
      totalFee,
      totalSettlement,
      merchantCount,
      totalPaymentText: totalPayment.toLocaleString(),
      totalFeeText: totalFee.toLocaleString(),
      totalSettlementText: totalSettlement.toLocaleString(),
    };
  }, [groupedList]);

  /** 기간 조회 전체 엑셀 다운로드 (가맹점별 요약) */
  const downloadAllExcel = () => {
    const escapeCell = (v) => (v == null ? '' : String(v).replace(/"/g, '""'));
    const header = '"가맹점명","결제금액","수수료","정산금액"';
    const lines = groupedList.map((item) =>
      [
        escapeCell(item.name),
        escapeCell(item.paymentAmount ?? 0),
        escapeCell(item.fee ?? 0),
        escapeCell(item.settlementAmount ?? 0),
      ].map((c) => `"${c}"`).join(',')
    );
    const csv = '\uFEFF' + header + '\r\n' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const monthLabel = selectedMonth.replace('-', '.');
    link.download = `세금계산서_${monthLabel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast('엑셀 다운로드가 완료되었습니다.');
  };

  return (
    <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden bg-white py-5 px-8">
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        {/* 월별 기간 조회 + 엑셀 다운로드 */}
        <div className="flex items-center justify-between gap-3 shrink-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className="h-[42px] flex items-center gap-1.5 bg-[#f2f4f6] px-3 rounded-lg border border-transparent focus-within:border-[var(--brand-primary)] transition-all">
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) setSelectedMonth(val);
                }}
                className="bg-transparent border-none outline-none text-[14px] font-mono text-[#191f28] py-0 h-full"
              />
            </div>
            <div className="flex gap-1">
              {['당월', '전월'].map((type) => (
                <button
                  key={type}
                  onClick={() => quickPeriod(type)}
                  className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[42px] flex items-center"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={downloadAllExcel}
            disabled={loading || groupedList.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors shrink-0 h-[42px] disabled:opacity-50 disabled:cursor-not-allowed"
            title="엑셀 다운로드"
          >
            <Download size={16} /> 엑셀다운로드
          </button>
        </div>

        {/* 통계 판넬 */}
        <div className="grid grid-cols-4 gap-4 shrink-0">
          <div className="bg-white rounded-[6px] border border-[#e5e8eb] p-4">
            <p className="text-[11px] font-bold text-[#8b95a1] mb-1">가맹점 수</p>
            <p className="text-[20px] font-black text-[#191f28]">{summaryStats.merchantCount.toLocaleString()}곳</p>
          </div>
          <div className="bg-white rounded-[6px] border border-[#e5e8eb] p-4">
            <p className="text-[11px] font-bold text-[#8b95a1] mb-1">총 결제금액</p>
            <p className="text-[20px] font-black text-[#191f28] tabular-nums">
              {summaryStats.totalPaymentText}원
            </p>
          </div>
          <div className="bg-white rounded-[6px] border border-[#e5e8eb] p-4">
            <p className="text-[11px] font-bold text-[#8b95a1] mb-1">총 수수료</p>
            <p className="text-[20px] font-black text-[var(--brand-primary)] tabular-nums">
              {summaryStats.totalFeeText}원
            </p>
          </div>
          <div className="bg-white rounded-[6px] border border-[#e5e8eb] p-4">
            <p className="text-[11px] font-bold text-[#8b95a1] mb-1">총 정산금액</p>
            <p className="text-[20px] font-black text-[#191f28] tabular-nums">
              {summaryStats.totalSettlementText}원
            </p>
          </div>
        </div>

        {/* 세금계산서 리스트 - 스크롤은 표 영역(body) 안에서만 */}
        <div className="flex-1 min-h-0 max-h-[480px] overflow-y-auto custom-scrollbar border border-[#e5e8eb] rounded-[6px] bg-white">
          {loading ? (
            <p className="text-[14px] text-[#8b95a1] py-8 text-center">불러오는 중...</p>
          ) : groupedList.length === 0 ? (
            <p className="text-[14px] text-[#8b95a1] py-8 text-center">해당 기간에 포함된 가맹점이 없습니다.</p>
          ) : (
              <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-[#f9fafb] border-b border-[#e5e8eb] text-[#8b95a1]">
                <tr>
                  <th className="px-4 py-3 text-left text-[12px] font-bold">가맹점</th>
                  <th className="px-4 py-3 text-right text-[12px] font-bold">결제금액</th>
                  <th className="px-4 py-3 text-right text-[12px] font-bold">정산금액</th>
                  <th className="px-4 py-3 text-right text-[12px] font-bold">수수료</th>
                  <th className="px-4 py-3 text-right text-[12px] font-bold pr-4">수수료율</th>
                </tr>
              </thead>
              <tbody>
                {groupedList.map((item) => {
                  const paymentAmount = Number(item.paymentAmount || 0);
                  const fee = Number(item.fee || 0);
                  const settlementAmount = Number(item.settlementAmount || 0);
                  const feeRate = paymentAmount > 0 ? (fee / paymentAmount) * 100 : 0;
                  return (
                    <tr key={item.id} className="border-b border-[#f2f4f6] hover:bg-[#f9fafb]">
                      <td className="px-4 py-3 text-left text-[14px] font-bold text-[#191f28] whitespace-nowrap">
                        {item.name}
                      </td>
                      <td className="px-4 py-3 text-right text-[14px] text-[#191f28] tabular-nums whitespace-nowrap">
                        {paymentAmount.toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 pr-4 text-right text-[14px] font-bold text-[#191f28] tabular-nums whitespace-nowrap">
                        {settlementAmount.toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 text-right text-[14px] text-[var(--brand-primary)] tabular-nums whitespace-nowrap">
                        {fee.toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 pr-4 text-right text-[14px] text-[#4e5968] tabular-nums whitespace-nowrap">
                        {feeRate.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaxInvoice;
